// src/components/InfoTooltip.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('info-tooltip')
export class InfoTooltip extends LitElement {
  createRenderRoot() { return this; }

  @property() description = '';
  @property({ type: Array }) examples: string[] = [];

  @state() private isOpen = false;
  @state() private tooltipElement: HTMLElement | null = null;

  private buttonRef: HTMLElement | null = null;
  private closeTimeout: number | null = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeTooltip();
    if (this.closeTimeout) window.clearTimeout(this.closeTimeout);
  }

  private handleMouseEnter(e: Event) {
    if (this.closeTimeout) {
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.buttonRef = e.currentTarget as HTMLElement;
    this.showTooltip();
  }

  private handleMouseLeave() {
    // Delay hiding to allow moving mouse to tooltip
    this.closeTimeout = window.setTimeout(() => {
      this.hideTooltip();
    }, 200);
  }

  private handleClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.buttonRef = e.currentTarget as HTMLElement;

    if (this.isOpen) {
      this.hideTooltip();
    } else {
      this.showTooltip();
    }
  }

  private showTooltip() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.createTooltip();
  }

  private hideTooltip() {
    this.isOpen = false;
    this.removeTooltip();
  }

  private createTooltip() {
    if (!this.buttonRef) return;

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = 'info-tooltip-portal';
    tooltip.style.opacity = '0';
    tooltip.style.visibility = 'hidden';
    tooltip.innerHTML = `
      <div class="rounded-lg border border-white/20 bg-slate-900/95 backdrop-blur-sm shadow-xl p-3 text-left w-56">
        <div class="text-sm font-semibold text-white mb-1">${this.description}</div>
        <div class="text-xs text-slate-300 space-y-0.5">
          <div class="font-medium text-slate-400">Examples:</div>
          ${this.examples.map(ex => `<div class="text-slate-300">• ${ex}</div>`).join('')}
        </div>
      </div>
    `;

    // Append to body first (so we can measure it)
    document.body.appendChild(tooltip);
    this.tooltipElement = tooltip;

    // Position tooltip after layout is complete, then show it
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.positionTooltip(tooltip);
        tooltip.style.opacity = '1';
        tooltip.style.visibility = 'visible';
      });
    });

    // Add event listeners to keep tooltip open when hovering over it
    tooltip.addEventListener('mouseenter', () => {
      if (this.closeTimeout) {
        window.clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    });

    tooltip.addEventListener('mouseleave', () => {
      this.closeTimeout = window.setTimeout(() => {
        this.hideTooltip();
      }, 200);
    });

    // Handle window resize/scroll
    const updatePosition = () => this.positionTooltip(tooltip);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Store cleanup function
    (tooltip as any).__cleanup = () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }

  private positionTooltip(tooltip: HTMLElement) {
    if (!this.buttonRef) return;

    const buttonRect = this.buttonRef.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    // Position above the button, centered
    let top = buttonRect.top - tooltipRect.height - 8;
    let left = buttonRect.left + (buttonRect.width / 2) - (tooltipRect.width / 2);

    // If tooltip would be cut off at top, show below instead
    if (top < 10) {
      top = buttonRect.bottom + 8;
    }

    // Prevent horizontal overflow
    const rightEdge = left + tooltipRect.width;
    if (rightEdge > window.innerWidth - 10) {
      left = window.innerWidth - tooltipRect.width - 10;
    }
    if (left < 10) {
      left = 10;
    }

    tooltip.style.position = 'fixed';
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.style.zIndex = '9999';
  }

  private removeTooltip() {
    if (this.tooltipElement) {
      // Call cleanup function if it exists
      if ((this.tooltipElement as any).__cleanup) {
        (this.tooltipElement as any).__cleanup();
      }
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  render() {
    return html`
      <button
        type="button"
        class="group/info relative p-0.5 rounded-full hover:bg-white/10 transition-colors"
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @click=${this.handleClick}
      >
        <svg class="h-3.5 w-3.5 text-slate-400 group-hover/info:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4M12 8h.01"/>
        </svg>
      </button>
    `;
  }
}
